/**
 * Pure scope-aware permission matching, with no database access.
 *
 * `scopeAccess.ts` answers "what scope does this user hold, and which farms
 * does that reach?", which needs the database. This module answers the
 * narrower question that comes up wherever a permission code is compared
 * against a list already in hand: does holding
 * `adminPanel:collection:create:farm` satisfy a requirement written as
 * `adminPanel:collection:create`?
 *
 * It does, and the reason it needs saying is that a plain `includes()` says
 * otherwise. That mismatch cost a farm caretaker every tool for recording
 * his day's work (see `tool-catalog.ts`'s `holdsScopedPermission` usage),
 * because the tool registry declares permissions unscoped — a tool is one
 * operation, whatever slice of the tenant a given user may run it over —
 * while the database grants them scoped.
 *
 * `resolveHeldScope` is the one matching rule for the whole app. The
 * route-level gate (`getUserPermissionScope`, behind `withTenantPermission`)
 * calls it on the list it loads from the database; the assistant's tool
 * catalog and proposal gate and the web panel's `hasPermission` call it on
 * the list they already hold. One function is what keeps "offered" and
 * "accepted" from drifting apart again. No Prisma import here, so client
 * components can use it too.
 */

/**
 * Scope levels, highest first: `global` (bare code), `farm` (`:farm`),
 * `owned` (`:owned`). `null` means the permission is not held at any scope.
 */
export type PermissionScope = 'global' | 'farm' | 'owned' | null;

const SCOPE_SUFFIXES: ReadonlySet<string> = new Set(['farm', 'owned']);

/**
 * Strips a scope suffix: `adminPanel:warehouse:update:farm` becomes
 * `adminPanel:warehouse:update`. Only `:farm` and `:owned` count as scopes;
 * any other fourth segment is part of the code, so `...:read:all` is never
 * widened into `...:read`.
 */
export function getBasePermission(permission: string): string {
  const parts = permission.split(':');
  return parts.length === 4 && SCOPE_SUFFIXES.has(parts[3] ?? '') ? parts.slice(0, 3).join(':') : permission;
}

/**
 * Every stored code that `resolveHeldScope` accepts for `permission`, for
 * database queries that must match the same grants (`code: { in: ... }`).
 */
export function scopedVariants(permission: string): string[] {
  const base = getBasePermission(permission);
  return [base, `${base}:farm`, `${base}:owned`];
}

/**
 * The highest scope at which `userPermissions` grants `required`, or `null`.
 *
 * `required` is normalised to its base code first, exactly as
 * `withTenantPermission` does, so a requirement written with a suffix
 * (`DASHBOARD_READ_FARM`) behaves the same here as at the endpoint.
 *
 * Matching is exact on the base code, then exact on the base code plus a
 * recognised scope suffix. It never matches on a shared prefix, so holding
 * `adminPanel:collection:read:farm` does not satisfy a requirement for
 * `adminPanel:collection:readSomethingElse`.
 */
export function resolveHeldScope(
  userPermissions: ReadonlySet<string> | readonly string[],
  required: string
): PermissionScope {
  const held: ReadonlySet<string> =
    userPermissions instanceof Set ? userPermissions : new Set(userPermissions as readonly string[]);
  const base = getBasePermission(required);
  if (held.has(base)) return 'global';
  if (held.has(`${base}:farm`)) return 'farm';
  if (held.has(`${base}:owned`)) return 'owned';
  return null;
}

/**
 * True when `userPermissions` grants `required` at any scope.
 *
 * This is a convenience/UX check, never the security boundary. Whatever it
 * lets through is still re-checked at the endpoint by `withTenantPermission`,
 * which resolves the real scope and narrows the rows to
 * `getAccessibleFarmIds`.
 */
export function holdsScopedPermission(userPermissions: ReadonlySet<string>, required: string): boolean {
  return resolveHeldScope(userPermissions, required) !== null;
}

/** Array-taking convenience for callers that hold a list rather than a set. */
export function holdsScopedPermissionFrom(userPermissions: readonly string[], required: string): boolean {
  return resolveHeldScope(userPermissions, required) !== null;
}

/** True when at least one of `required` is held at any scope. */
export function holdsAnyScopedPermission(userPermissions: readonly string[], required: readonly string[]): boolean {
  const held = new Set(userPermissions);
  return required.some((code) => resolveHeldScope(held, code) !== null);
}

/** True when every one of `required` is held at some scope. */
export function holdsAllScopedPermissions(userPermissions: readonly string[], required: readonly string[]): boolean {
  const held = new Set(userPermissions);
  return required.every((code) => resolveHeldScope(held, code) !== null);
}
